import { useState, useEffect, useRef, useCallback } from 'react';
import { RealTimeUpdate } from '../types/analytics';

interface UseWebSocketOptions {
  url: string;
  enabled?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

interface WebSocketState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  lastMessage: any;
  reconnectAttempts: number;
}

export const useWebSocket = ({
  url,
  enabled = true,
  reconnectInterval = 3000,
  maxReconnectAttempts = 5
}: UseWebSocketOptions) => {
  const [state, setState] = useState<WebSocketState>({
    connected: false,
    connecting: false,
    error: null,
    lastMessage: null,
    reconnectAttempts: 0
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageHandlersRef = useRef<Map<string, (data: any) => void>>(new Map());

  const connect = useCallback(() => {
    if (!enabled || state.connecting || state.connected) return;

    setState(prev => ({ ...prev, connecting: true, error: null }));

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setState(prev => ({
          ...prev,
          connected: true,
          connecting: false,
          error: null,
          reconnectAttempts: 0
        }));
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setState(prev => ({ ...prev, lastMessage: message }));
          
          // Call registered message handlers
          const handler = messageHandlersRef.current.get(message.type);
          if (handler) {
            handler(message.data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        setState(prev => {
          const newState = { ...prev, connected: false, connecting: false };
          wsRef.current = null;

          if (enabled && prev.reconnectAttempts < maxReconnectAttempts) {
            console.log(`WebSocket closed. Attempting to reconnect... (${prev.reconnectAttempts + 1}/${maxReconnectAttempts})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              setState(prevState => ({ ...prevState, reconnectAttempts: prevState.reconnectAttempts + 1 }));
              connect();
            }, reconnectInterval);
          } else if (prev.reconnectAttempts >= maxReconnectAttempts) {
            newState.error = 'Maximum reconnection attempts reached';
          }

          return newState;
        });
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setState(prev => ({ 
          ...prev, 
          error: 'WebSocket connection error',
          connecting: false 
        }));
      };

    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to create WebSocket connection',
        connecting: false 
      }));
    }
  }, [url, enabled, reconnectInterval, maxReconnectAttempts, state.connecting, state.connected]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setState({
      connected: false,
      connecting: false,
      error: null,
      lastMessage: null,
      reconnectAttempts: 0
    });
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current && state.connected) {
      try {
        wsRef.current.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        return false;
      }
    }
    return false;
  }, [state.connected]);

  const subscribe = useCallback((messageType: string, handler: (data: any) => void) => {
    messageHandlersRef.current.set(messageType, handler);
    
    // Send subscription message if connected
    if (state.connected) {
      sendMessage({ type: 'subscribe', channel: messageType });
    }

    return () => {
      messageHandlersRef.current.delete(messageType);
      if (state.connected) {
        sendMessage({ type: 'unsubscribe', channel: messageType });
      }
    };
  }, [state.connected, sendMessage]);

  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    sendMessage,
    subscribe
  };
};

// Hook specifically for sales analytics real-time updates
export const useSalesRealTime = (enabled: boolean = true) => {
  const [salesUpdates, setSalesUpdates] = useState<RealTimeUpdate[]>([]);
  const [notifications, setNotifications] = useState<string[]>([]);

  const webSocketUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:3000/admin';
  
  const { connected, error, subscribe } = useWebSocket({
    url: webSocketUrl,
    enabled
  });

  useEffect(() => {
    if (!connected) return;

    // Subscribe to sales-related updates
    const unsubscribeBookings = subscribe('booking_updates', (data: RealTimeUpdate) => {
      setSalesUpdates(prev => [data, ...prev.slice(0, 49)]); // Keep last 50 updates
      
      // Add notification
      if (data.type === 'booking_created') {
        setNotifications(prev => [
          `New booking created: ${data.data.bookingId}`,
          ...prev.slice(0, 9)
        ]);
      } else if (data.type === 'booking_completed') {
        setNotifications(prev => [
          `Booking completed: ${data.data.bookingId} - Revenue: $${data.data.revenue}`,
          ...prev.slice(0, 9)
        ]);
      }
    });

    const unsubscribeRevenue = subscribe('revenue_updates', (data: RealTimeUpdate) => {
      setSalesUpdates(prev => [data, ...prev.slice(0, 49)]);
      
      if (data.type === 'revenue_update') {
        setNotifications(prev => [
          `Revenue updated: $${data.data.revenue}`,
          ...prev.slice(0, 9)
        ]);
      }
    });

    return () => {
      unsubscribeBookings();
      unsubscribeRevenue();
    };
  }, [connected, subscribe]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const clearUpdates = useCallback(() => {
    setSalesUpdates([]);
  }, []);

  return {
    connected,
    error,
    salesUpdates,
    notifications,
    clearNotifications,
    clearUpdates
  };
};